import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { CountUpDirective } from '../../../../directives/count-up.directive';
import { PrettyNumberPipe } from '../../../../pipes/pretty-number.pipe';
import { ChartConfiguration, ChartData, ChartOptions, Plugin } from 'chart.js';
import { DataPointCardConfig, DataPointCardData } from '../card.component';
import { ChartThemeService } from '../../../../services/chart-theme/chart-theme.service';

interface MiniChartConfig {
  data: ChartData<'doughnut', number[], string>;
  options: ChartOptions<'doughnut'>;
}

@Component({
  selector: 'app-data-point-card-content',
  imports: [
    CommonModule,
    CountUpDirective,
    PrettyNumberPipe,
    BaseChartDirective,
  ],
  templateUrl: './data-point-card-content.component.html',
  styleUrl: './data-point-card-content.component.scss',
})
export class DataPointCardContentComponent {
  private _config!: DataPointCardConfig;
  private readonly _defaultColor = '#2ed9c2';
  private readonly _remainderColor = 'rgba(13, 30, 51, 0.12)';
  private readonly _accentActiveColor = '#ffffff';
  private readonly _accentRemainderColor = 'rgba(255, 255, 255, 0.28)';

  readonly chartType: ChartConfiguration<'doughnut'>['type'] = 'doughnut';
  readonly chartPlugins: Plugin<'doughnut'>[] = [];
  miniCharts: (MiniChartConfig | null)[] = [];

  constructor(private readonly _theme: ChartThemeService) {}

  @Input()
  set config(value: DataPointCardConfig) {
    this._config = value;
    this._buildMiniCharts();
  }

  get config(): DataPointCardConfig {
    return this._config;
  }

  miniChart(index: number): MiniChartConfig | null {
    return this.miniCharts[index] ?? null;
  }

  private _buildMiniCharts(): void {
    const config = this._config;
    if (!config?.data?.length) {
      this.miniCharts = [];
      return;
    }

    const dataPoints = config.data;
    const palette = this._resolvePalette(
      dataPoints.length,
      config.colorIndex ?? 0
    );

    this.miniCharts = dataPoints.map((dataPoint, index) =>
      this._createMiniChart(dataPoint, palette, index)
    );
  }

  private _shouldRenderChart(
    dataPoint: DataPointCardData | undefined
  ): boolean {
    if (!dataPoint) {
      return false;
    }
    const unit = (dataPoint.unit ?? '').trim();
    if (unit !== '%') {
      return false;
    }

    if (!dataPoint.addPieChart) {
      return false;
    }

    const numeric = Number(dataPoint.value);
    return Number.isFinite(numeric) && Math.abs(numeric) > 0;
  }

  private _sanitizePercent(value: number | undefined): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric === 0) {
      return 0;
    }
    const magnitude = Math.abs(numeric);
    return magnitude >= 100 ? 100 : magnitude;
  }

  private _calculateRemainder(value: number): number {
    const remainder = 100 - value;
    return remainder > 0 ? remainder : 0;
  }

  private _resolvePalette(count: number, colorIndex: number): string[] {
    const palette = this._theme.getSeriesColors(colorIndex, {
      seriesCount: count,
    });
    return palette.length ? palette : [this._defaultColor];
  }

  private _createMiniChart(
    dataPoint: DataPointCardData,
    palette: string[],
    index: number
  ): MiniChartConfig | null {
    if (!this._shouldRenderChart(dataPoint)) {
      return null;
    }

    const percentValue = this._sanitizePercent(dataPoint.value);
    if (percentValue <= 0) {
      return null;
    }

    const remainder = this._calculateRemainder(percentValue);
    const useAccent = !!this._config?.accent;
    const activeColor = this._resolveActiveColor(palette, index, useAccent);
    const remainderColor = useAccent
      ? this._accentRemainderColor
      : this._remainderColor;

    const datasetValues =
      remainder > 0 ? [percentValue, remainder] : [percentValue];
    const backgroundColors =
      remainder > 0 ? [activeColor, remainderColor] : [activeColor];

    return {
      data: {
        labels: ['value', 'remaining'],
        datasets: [
          {
            data: datasetValues,
            backgroundColor: backgroundColors,
            borderWidth: 0,
            hoverBorderWidth: 0,
            hoverOffset: 0,
          },
        ],
      },
      options: this._buildChartOptions(),
    };
  }

  private _resolveActiveColor(
    palette: string[],
    index: number,
    useAccent: boolean
  ): string {
    if (useAccent) {
      return this._accentActiveColor;
    }
    return palette[index % palette.length] ?? this._defaultColor;
  }

  private _buildChartOptions(): ChartOptions<'doughnut'> {
    return {
      maintainAspectRatio: false,
      responsive: true,
      cutout: '60%',
      events: [],
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
    };
  }
}
